<template>
  <div class="p-3 sm:p-4 text-lexend min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- HEADER -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
      <div class="w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-teal-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-teal-600 tracking-widest">Recarga de Gas</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white flex items-center gap-2 flex-wrap">
          <Icon icon="mdi:map-marker-radius" class="text-teal-600" />
          Zonas de Cobertura
          <span class="px-2 py-0.5 bg-teal-100 dark:bg-teal-900/40 text-teal-600 text-xs font-black rounded-lg">
            {{ zonas.length }}
          </span>
        </h1>
      </div>

      <button
        @click="openCreateModal"
        class="w-full sm:w-auto px-4 py-3 sm:py-2 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl font-black text-xs tracking-widest shadow-md flex items-center justify-center gap-2 active:scale-95 transition"
      >
        <Icon icon="mdi:plus-circle" class="text-base" />
        Nueva Zona
      </button>
    </div>

    <!-- FILTROS -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 mb-4">
      <div class="relative mb-3">
        <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
        <input
          v-model="searchQuery"
          placeholder="Buscar zona o ciudad..."
          class="w-full pl-10 pr-3 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-teal-500 rounded-xl outline-none dark:text-white font-bold text-sm"
        />
      </div>

      <div class="grid grid-cols-2 gap-2">
        <select
          v-model="filterStatus"
          @change="loadZonas"
          class="w-full px-3 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 rounded-xl font-black text-sm dark:text-white outline-none"
        >
          <option value="all">TODAS</option>
          <option value="activas">ACTIVAS</option>
          <option value="inactivas">INACTIVAS</option>
        </select>
        <button
          @click="loadZonas"
          class="w-full px-3 py-3 bg-gray-100 dark:bg-gray-700 rounded-xl font-black text-xs flex items-center justify-center gap-1 dark:text-white active:scale-95 transition"
        >
          <Icon icon="mdi:refresh" :class="{'animate-spin': loading}" class="text-base" />
          Actualizar
        </button>
      </div>
    </div>

    <!-- LOADING -->
    <div v-if="loading" class="space-y-3">
      <div v-for="i in 3" :key="'l'+i" class="bg-white dark:bg-gray-800 rounded-2xl p-4 animate-pulse border border-gray-200 dark:border-gray-700">
        <div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-1/3 mb-3"></div>
        <div class="h-3 bg-gray-100 dark:bg-gray-700 rounded w-2/3"></div>
      </div>
    </div>

    <!-- EMPTY -->
    <div v-else-if="filteredZonas.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-10 text-center">
      <Icon icon="mdi:map-marker-off" class="text-6xl mb-3 mx-auto text-gray-300" />
      <p class="text-sm font-black text-gray-400">No hay zonas registradas</p>
    </div>

    <!-- ═══════════════════════════════════════════════ -->
    <!-- 📱 MÓVIL: TARJETAS -->
    <!-- ═══════════════════════════════════════════════ -->
    <div v-else class="lg:hidden space-y-3">
      <div
        v-for="z in filteredZonas"
        :key="'m-'+z.idZona"
        class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
      >
        <!-- Header: color según estado -->
        <div :class="[
          'px-4 py-2.5 flex items-center justify-between',
          z.estadoZona === 'activo'
            ? 'bg-gradient-to-r from-teal-600 to-emerald-600'
            : 'bg-gradient-to-r from-rose-500 to-red-500'
        ]">
          <div class="flex items-center gap-2 min-w-0">
            <Icon icon="mdi:map-marker" class="text-white text-lg flex-shrink-0" />
            <span class="font-black text-white text-sm truncate">{{ z.nombre }}</span>
          </div>
          <span class="px-2 py-0.5 bg-white/20 text-white text-[9px] font-black uppercase rounded whitespace-nowrap">
            {{ z.estadoZona }}
          </span>
        </div>

        <!-- Descripción -->
        <div v-if="z.descripcion" class="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
          <p class="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">{{ z.descripcion }}</p>
        </div>

        <!-- Ubicación -->
        <div class="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
          <div class="flex items-start gap-2">
            <div class="w-8 h-8 rounded-lg bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center flex-shrink-0">
              <Icon icon="mdi:city" class="text-teal-600 dark:text-teal-400 text-sm" />
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs font-black text-gray-900 dark:text-white">
                {{ z.ciudad || '-' }}
                <span v-if="z.codigoPostal" class="text-teal-600 dark:text-teal-400">({{ z.codigoPostal }})</span>
              </p>
              <p class="text-[10px] text-gray-500">{{ z.estado || '-' }} · {{ z.pais || 'Venezuela' }}</p>
            </div>
          </div>
        </div>

        <!-- Costo / Radio / Tiempo (chips) -->
        <div class="px-4 py-3 border-b border-gray-100 dark:border-gray-700 grid grid-cols-3 gap-2">
          <div class="bg-emerald-50 dark:bg-emerald-900/30 rounded-lg p-2 text-center">
            <p class="text-[9px] font-black text-emerald-600 dark:text-emerald-400 uppercase">Delivery</p>
            <p class="text-sm font-black text-emerald-700 dark:text-emerald-300">
              ${{ Number(z.costoDelivery || 0).toFixed(2) }}
            </p>
          </div>
          <div class="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-2 text-center">
            <p class="text-[9px] font-black text-blue-600 dark:text-blue-400 uppercase">Radio</p>
            <p class="text-sm font-black text-blue-700 dark:text-blue-300">
              {{ z.radioCoberturaKm || '-' }} <span class="text-[10px]">km</span>
            </p>
          </div>
          <div class="bg-amber-50 dark:bg-amber-900/30 rounded-lg p-2 text-center">
            <p class="text-[9px] font-black text-amber-600 dark:text-amber-400 uppercase">Tiempo</p>
            <p class="text-sm font-black text-amber-700 dark:text-amber-300">
              {{ z.tiempoEstimadoMinutos || '-' }} <span class="text-[10px]">min</span>
            </p>
          </div>
        </div>

        <!-- Clientes -->
        <div class="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <span class="inline-flex items-center gap-1.5 text-[11px] font-black text-gray-600 dark:text-gray-300">
            <Icon icon="mdi:account-group" class="text-blue-500 text-base" />
            Clientes en esta zona
          </span>
          <span class="px-2.5 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-lg text-xs font-black">
            {{ z.totalClientes || 0 }}
          </span>
        </div>

        <!-- Botones -->
        <div class="px-3 py-3 bg-gray-50 dark:bg-gray-900/30 flex gap-2">
          <button
            @click="openEditModal(z)"
            class="flex-1 py-2.5 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-xl font-black text-[11px] uppercase flex items-center justify-center gap-1.5 active:scale-95 transition shadow-sm"
          >
            <Icon icon="mdi:pencil" class="text-base" />
            Editar
          </button>
          <button
            @click="deleteZona(z.idZona)"
            class="flex-1 py-2.5 bg-gradient-to-br from-rose-500 to-red-600 text-white rounded-xl font-black text-[11px] uppercase flex items-center justify-center gap-1.5 active:scale-95 transition shadow-sm"
          >
            <Icon icon="mdi:delete" class="text-base" />
            Eliminar
          </button>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════ -->
    <!-- 💻 DESKTOP: TABLA -->
    <!-- ═══════════════════════════════════════════════ -->
    <div v-if="!loading && filteredZonas.length > 0" class="hidden lg:block bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-left">
          <thead class="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
            <tr>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">ZONA</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">UBICACIÓN</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-right">DELIVERY</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">RADIO</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">TIEMPO</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">CLIENTES</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">ESTADO</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-right">ACCIONES</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-for="z in filteredZonas" :key="z.idZona" class="hover:bg-teal-50/30 dark:hover:bg-gray-700/30">
              <td class="px-5 py-4">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-100 to-emerald-100 dark:from-teal-900 dark:to-emerald-900 flex items-center justify-center flex-shrink-0">
                    <Icon icon="mdi:map-marker" class="text-teal-600 dark:text-teal-300" />
                  </div>
                  <div>
                    <p class="text-sm font-black text-gray-900 dark:text-white">{{ z.nombre }}</p>
                    <p class="text-[10px] text-gray-500">{{ z.descripcion || 'Sin descripción' }}</p>
                  </div>
                </div>
              </td>
              <td class="px-5 py-4 text-xs">
                <p class="font-bold text-gray-800 dark:text-gray-200">
                  {{ z.ciudad || '-' }}
                  <span v-if="z.codigoPostal" class="text-teal-600">({{ z.codigoPostal }})</span>
                </p>
                <p class="text-[10px] text-gray-500">{{ z.estado || '-' }} · {{ z.pais || 'Venezuela' }}</p>
              </td>
              <td class="px-5 py-4 text-right text-sm font-black text-emerald-600">
                ${{ Number(z.costoDelivery || 0).toFixed(2) }}
              </td>
              <td class="px-5 py-4 text-center text-xs font-bold text-gray-700 dark:text-gray-300">
                {{ z.radioCoberturaKm || '-' }} km
              </td>
              <td class="px-5 py-4 text-center text-xs font-bold text-gray-700 dark:text-gray-300">
                {{ z.tiempoEstimadoMinutos || '-' }} min
              </td>
              <td class="px-5 py-4 text-center">
                <span class="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-xs font-black">
                  {{ z.totalClientes || 0 }}
                </span>
              </td>
              <td class="px-5 py-4 text-center">
                <span :class="['px-3 py-1 text-[10px] font-black uppercase rounded-lg border', z.estadoZona === 'activo' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800' : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800']">
                  {{ z.estadoZona }}
                </span>
              </td>
              <td class="px-5 py-4 text-right">
                <div class="flex items-center justify-end gap-1">
                  <button @click="openEditModal(z)" class="p-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 rounded-lg hover:bg-amber-600 hover:text-white transition-all">
                    <Icon icon="mdi:pencil" />
                  </button>
                  <button @click="deleteZona(z.idZona)" class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 rounded-lg hover:bg-rose-600 hover:text-white transition-all">
                    <Icon icon="mdi:delete" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════ -->
    <!-- MODAL FORM (bottom-sheet en móvil) -->
    <!-- ═══════════════════════════════════════════════ -->
    <Teleport to="body">
      <Transition name="modal">
        <div
          v-if="showModal"
          class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-[9998]"
          @click.self="showModal = false"
        >
          <div class="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
            <!-- Header -->
            <div class="bg-gradient-to-r from-teal-600 to-emerald-600 px-5 py-4 flex items-center gap-3 flex-shrink-0 sm:rounded-t-2xl">
              <Icon :icon="editing ? 'mdi:pencil-box' : 'mdi:map-marker-plus'" class="text-white text-xl" />
              <h3 class="text-sm font-black text-white">{{ editing ? 'Editar Zona' : 'Nueva Zona' }}</h3>
              <button @click="closeModal" class="ml-auto w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white active:scale-95 transition">
                <Icon icon="mdi:close" />
              </button>
            </div>

            <!-- Body -->
            <div class="p-4 sm:p-6 overflow-y-auto flex-1">
              <form @submit.prevent="save" class="space-y-4">
                <!-- Nombre + Código Postal -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-1.5">Nombre *</label>
                    <input v-model="form.nombre" required
                      class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-teal-500 rounded-xl text-sm dark:text-white outline-none"
                      placeholder="Ej: Zona Centro" />
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-1.5">Código Postal</label>
                    <input v-model="form.codigoPostal" readonly
                      class="w-full px-4 py-3 bg-gray-100 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 rounded-xl text-sm dark:text-gray-300 outline-none cursor-not-allowed"
                      placeholder="Se asigna por ciudad" />
                  </div>
                </div>

                <!-- Descripción -->
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-1.5">Descripción</label>
                  <textarea v-model="form.descripcion" rows="2"
                    class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-teal-500 rounded-xl text-sm dark:text-white outline-none resize-none"></textarea>
                </div>

                <!-- Ciudad + Estado + País -->
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-1.5">Ciudad *</label>
                    <select v-model="form.ciudad" @change="onCiudadChange" required
                      class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-teal-500 rounded-xl text-sm dark:text-white outline-none">
                      <option value="">-- Seleccione ciudad --</option>
                      <option v-for="c in ciudadesLara" :key="c.ciudad" :value="c.ciudad">{{ c.ciudad }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-1.5 flex items-center gap-1">
                      <Icon icon="mdi:lock" class="text-teal-600" /> Estado
                    </label>
                    <input v-model="form.estado" readonly
                      class="w-full px-4 py-3 bg-gray-100 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 rounded-xl text-sm dark:text-gray-300 outline-none cursor-not-allowed" />
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-1.5 flex items-center gap-1">
                      <Icon icon="mdi:lock" class="text-teal-600" /> País
                    </label>
                    <input v-model="form.pais" readonly
                      class="w-full px-4 py-3 bg-gray-100 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 rounded-xl text-sm dark:text-gray-300 outline-none cursor-not-allowed" />
                  </div>
                </div>

                <!-- Costo / Radio / Tiempo -->
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-1.5">Costo Delivery ($)</label>
                    <input v-model.number="form.costoDelivery" type="number" step="0.01" min="0"
                      class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 rounded-xl text-sm dark:text-white outline-none" />
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-1.5">Radio (km)</label>
                    <input v-model.number="form.radioCoberturaKm" type="number" step="0.1" min="0"
                      class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 rounded-xl text-sm dark:text-white outline-none" />
                  </div>
                  <div>
                    <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-1.5">Tiempo (min)</label>
                    <input v-model.number="form.tiempoEstimadoMinutos" type="number" min="0"
                      class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 rounded-xl text-sm dark:text-white outline-none" />
                  </div>
                </div>

                <!-- Estado (zona activa/inactiva) -->
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-1.5">Estado de la zona</label>
                  <select v-model="form.estadoZona"
                    class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 rounded-xl text-sm dark:text-white outline-none">
                    <option value="activo">ACTIVA</option>
                    <option value="inactivo">INACTIVA</option>
                  </select>
                </div>

                <!-- Botones -->
                <div class="flex flex-col-reverse sm:flex-row gap-2 pt-4 border-t dark:border-gray-700">
                  <button type="button" @click="closeModal"
                    class="w-full sm:flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl font-black text-xs dark:text-gray-300 active:scale-95 transition">
                    Cancelar
                  </button>
                  <button type="submit" :disabled="saving"
                    class="w-full sm:flex-[2] py-3 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl font-black text-xs uppercase disabled:opacity-50 flex items-center justify-center gap-2 active:scale-95 transition shadow-md">
                    <Icon :icon="saving ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': saving}" class="text-base" />
                    {{ saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear') }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { obtenerZonas, crearZona, actualizarZona, eliminarZona } from '@/api/gas.js';

export default {
  name: 'ZonasList',
  components: { Icon },
  data() {
    return {
      zonas: [],
      loading: false,
      saving: false,
      showModal: false,
      editing: null,
      searchQuery: '',
      filterStatus: 'all',

      ciudadesLara: [
        { ciudad: 'Agua Viva', codigo: '3023' },
        { ciudad: 'Aguada Grande', codigo: '3031' },
        { ciudad: 'Altagracia', codigo: '3050' },
        { ciudad: 'Anzoátegui', codigo: '3018' },
        { ciudad: 'Aregue', codigo: '3052' },
        { ciudad: 'Atarigua', codigo: '3050' },
        { ciudad: 'Baragua', codigo: '3034' },
        { ciudad: 'Barbacoas', codigo: '3050' },
        { ciudad: 'Barquisimeto', codigo: '3001' },
        { ciudad: 'Bobare', codigo: '3004' },
        { ciudad: 'Bojó', codigo: '3028' },
        { ciudad: 'Buena Vista', codigo: '3001' },
        { ciudad: 'Cabudare', codigo: '3023' },
        { ciudad: 'Carora', codigo: '3050' },
        { ciudad: 'Cuara', codigo: '3061' },
        { ciudad: 'Cubiro', codigo: '3063' },
        { ciudad: 'Curarigua', codigo: '3051' },
        { ciudad: 'Duaca', codigo: '3025' },
        { ciudad: 'El Blanco', codigo: '3050' },
        { ciudad: 'El Ceibal', codigo: '3061' },
        { ciudad: 'El Cují', codigo: '3001' },
        { ciudad: 'El Manzano', codigo: '3023' },
        { ciudad: 'El Tocuyo', codigo: '3018' },
        { ciudad: 'Eneal', codigo: '3025' },
        { ciudad: 'Guadalupe', codigo: '3050' },
        { ciudad: 'Guárico', codigo: '3057' },
        { ciudad: 'Humocaro Alto', codigo: '3058' },
        { ciudad: 'Humocaro Bajo', codigo: '3058' },
        { ciudad: 'Jabón', codigo: '3050' },
        { ciudad: 'La Miel', codigo: '3015' },
        { ciudad: 'Las Mercedes', codigo: '3050' },
        { ciudad: 'Licua', codigo: '3025' },
        { ciudad: 'Los Rastrojos', codigo: '3023' },
        { ciudad: 'Manzanita', codigo: '3015' },
        { ciudad: 'Palmarito', codigo: '3050' },
        { ciudad: 'Quebrada Arriba', codigo: '3050' },
        { ciudad: 'Quebrada Honda de Guache', codigo: '3028' },
        { ciudad: 'Quíbor', codigo: '3061' },
        { ciudad: 'Río Claro', codigo: '3009' },
        { ciudad: 'Sanare', codigo: '3028' },
        { ciudad: 'San Miguel', codigo: '3016' },
        { ciudad: 'San Pedro', codigo: '3056' },
        { ciudad: 'Santa Inés', codigo: '3001' },
        { ciudad: 'Santa Rosa', codigo: '3001' },
        { ciudad: 'Sarare', codigo: '3015' },
        { ciudad: 'Siquisique', codigo: '3032' },
        { ciudad: 'Tamaca', codigo: '3001' },
        { ciudad: 'Tintorero', codigo: '3061' },
        { ciudad: 'Yacambú', codigo: '3028' }
      ],

      form: {
        nombre: '',
        descripcion: '',
        codigoPostal: '',
        ciudad: '',
        estado: 'Lara',
        pais: 'Venezuela',
        latitudCentro: null,
        longitudCentro: null,
        radioCoberturaKm: null,
        costoDelivery: 0,
        tiempoEstimadoMinutos: null,
        estadoZona: 'activo'
      }
    };
  },
  computed: {
    filteredZonas() {
      let list = [...this.zonas];
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(z =>
          (z.nombre || '').toLowerCase().includes(q) ||
          (z.ciudad || '').toLowerCase().includes(q)
        );
      }
      return list;
    }
  },
  async mounted() { await this.loadZonas(); },
  methods: {
    async loadZonas() {
      this.loading = true;
      try {
        const res = await obtenerZonas(this.filterStatus);
        if (res.success) this.zonas = res.data;
      } finally { this.loading = false; }
    },

    onCiudadChange() {
      const c = this.ciudadesLara.find(x => x.ciudad === this.form.ciudad);
      this.form.codigoPostal = c ? c.codigo : '';
    },

    openCreateModal() {
      this.editing = null;
      this.form = {
        nombre: '',
        descripcion: '',
        codigoPostal: '',
        ciudad: '',
        estado: 'Lara',
        pais: 'Venezuela',
        latitudCentro: null,
        longitudCentro: null,
        radioCoberturaKm: null,
        costoDelivery: 0,
        tiempoEstimadoMinutos: null,
        estadoZona: 'activo'
      };
      this.showModal = true;
    },

    openEditModal(z) {
      this.editing = z;
      this.form = {
        ...z,
        estado: 'Lara',
        pais: 'Venezuela'
      };
      this.showModal = true;
    },

    closeModal() { this.showModal = false; },

    async save() {
      if (!this.form.ciudad) {
        alert('Debes seleccionar una ciudad');
        return;
      }
      this.saving = true;
      try {
        const res = this.editing
          ? await actualizarZona(this.editing.idZona, this.form)
          : await crearZona(this.form);
        if (res.success) { await this.loadZonas(); this.closeModal(); }
        else alert(res.error || 'Error al guardar');
      } finally { this.saving = false; }
    },

    async deleteZona(id) {
      if (!confirm('¿Eliminar esta zona?')) return;
      const res = await eliminarZona(id);
      if (res.success) await this.loadZonas();
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }

@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }

/* Transición de modal */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}
.modal-enter-active > div,
.modal-leave-active > div {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from > div,
.modal-leave-to > div {
  transform: translateY(30px) scale(0.95);
  opacity: 0;
}

/* Evitar zoom en iOS */
input, textarea, select {
  font-size: 16px;
}

* {
  -webkit-tap-highlight-color: transparent;
}
</style>